import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { useSelectedLocation } from 'contexts';
import { useCallback, useMemo } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

const DATE_FORMAT = 'YYYY-MM-DD';
const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

/* getZoned = convert date or datetime-local inputs (onChange value) to selectedLocation.timezone, return as utc */
/* parseZoned = convert utc dates to selectedLocation.timezone and format for date or datetime-local input values */

const useTimeZone = () => {
  const { selectedLocation } = useSelectedLocation();

  const timeZoneIndicator = useMemo(() => dayjs().tz(selectedLocation?.timeZone).format('Z'), [selectedLocation]);

  const timeZoneOffset = useMemo(() => dayjs().tz(selectedLocation?.timeZone).utcOffset() / 60, [selectedLocation]);

  /* alternative to calling just dayjs() -- applies selected location timezone offset */
  const newZonedDayJs = useCallback(
    (date?: dayjs.ConfigType): dayjs.Dayjs => {
      return dayjs.utc(date).tz(selectedLocation?.timeZone);
    },
    [selectedLocation]
  );

  const zonedStartOfDay = useCallback(
    (date?: string): dayjs.Dayjs => {
      return dayjs.tz(date, selectedLocation?.timeZone).startOf('day');
    },
    [selectedLocation]
  );

  const zonedEndOfDay = useCallback(
    (date?: string): dayjs.Dayjs => {
      return dayjs.tz(date, selectedLocation?.timeZone).endOf('day');
    },
    [selectedLocation]
  );

  const dateInputToUtc = useCallback(
    (date: dayjs.ConfigType): string => {
      if (!date) return '';
      return dayjs.tz(date, DATE_FORMAT, selectedLocation?.timeZone).toISOString();
    },
    [selectedLocation]
  );

  const dateTimeLocalToUtc = useCallback(
    (date: dayjs.ConfigType): string => {
      if (!date) return '';
      return dayjs.tz(date, DATETIME_LOCAL_FORMAT, selectedLocation?.timeZone).toISOString();
    },
    [selectedLocation]
  );

  const utcToZonedDate = useCallback(
    (date?: dayjs.ConfigType): string => {
      if (!date) return '';
      const dateTime = dayjs
        .utc(typeof date !== 'string' ? date : date.replace(/[+-]\d\d:00/, ''))
        .tz(selectedLocation?.timeZone);
      return !dateTime.isValid() ? '' : dateTime.format(DATE_FORMAT);
    },
    [selectedLocation]
  );

  const utcToZonedDateTimeLocal = useCallback(
    (date?: dayjs.ConfigType): string => {
      if (!date) return '';
      const dateTime = dayjs
        .utc(typeof date !== 'string' ? date : date.replace(/[+-]\d\d:00/, ''))
        .tz(selectedLocation?.timeZone);
      return !dateTime.isValid() ? '' : dateTime.format(DATETIME_LOCAL_FORMAT);
    },
    [selectedLocation]
  );

  return {
    timeZoneIndicator,
    timeZoneOffset,
    dateInputToUtc,
    dateTimeLocalToUtc,
    newZonedDayJs,
    zonedStartOfDay,
    zonedEndOfDay,
    utcToZonedDate,
    utcToZonedDateTimeLocal
  };
};

export default useTimeZone;
